import { Pressable } from '@react-aria/interactions'
import { Item, Section } from '@react-stately/collections'
import { useState } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Voice } from 'blaze-ui/icons'
import { Check02 } from 'blaze-ui/icons/16/Check02'
import { Pause } from 'blaze-ui/icons/20/Pause'
import { VolumeOn } from 'blaze-ui/icons/20/VolumeOn'
import { Select } from 'components/Select/Select'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'
import {
  DEFAULT_VIDEO_VOICEOVER_VOICE,
  VIDEO_VOICEOVER_VOICES_FEMALE,
  VIDEO_VOICEOVER_VOICES_MALE,
} from '../videoVoiceoverCaptionsConstants'
import { VIDEO_VOICE_PREVIEW_URLS } from '../videoVoiceoverCaptionsUtils'
import Styles from './VideoVoiceoverPreference.module.scss'

type Props = {
  enabled: boolean
  voice: string
  onEnabledUpdate: (enabled: boolean) => void
  onVoiceUpdate: (voice: string) => void
}

export const getVoiceLabel = (voiceKey: string) => {
  const match = [...VIDEO_VOICEOVER_VOICES_FEMALE, ...VIDEO_VOICEOVER_VOICES_MALE].find(
    (v) => v.key === voiceKey,
  )
  if (!match) return voiceKey
  const [, tone] = match.label.split(' — ')
  return tone ? `${match.key} • ${tone}` : match.key
}

// Module-level singleton so only one clip plays at a time across the whole page
let activeAudio: HTMLAudioElement | null = null

const useVoicePreview = () => {
  const [playingKey, setPlayingKey] = useState<string | null>(null)

  const toggle = (voiceKey: string) => {
    const url = VIDEO_VOICE_PREVIEW_URLS[voiceKey as keyof typeof VIDEO_VOICE_PREVIEW_URLS]

    // Pause currently playing (same or different voice)
    if (activeAudio) {
      activeAudio.pause()
      activeAudio.onended = null
      activeAudio = null
    }

    // If we clicked the already-playing voice, just stop
    if (playingKey === voiceKey) {
      setPlayingKey(null)
      return
    }

    if (!url) return

    const audio = new Audio(url)
    activeAudio = audio
    setPlayingKey(voiceKey)
    audio.play()
    audio.onended = () => {
      activeAudio = null
      setPlayingKey(null)
    }
  }

  return { playingKey, toggle }
}

type VoiceItemContentProps = {
  voiceKey: string
  isSelected: boolean
  isPlaying: boolean
  onToggle: (key: string) => void
}

const VoiceItemContent = ({ voiceKey, isSelected, isPlaying, onToggle }: VoiceItemContentProps) => (
  <div className={Styles.voiceItemContent}>
    <span>{getVoiceLabel(voiceKey)}</span>
    {isSelected ? (
      <div className={Styles.voiceItemCheck}>
        <div className={Styles.voiceItemCheckInner}>
          <Check02 />
        </div>
      </div>
    ) : (
      <button
        type="button"
        className={Styles.speakerButton}
        aria-label={isPlaying ? `Pause ${voiceKey}` : `Preview ${voiceKey} voice`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onToggle(voiceKey)
        }}
      >
        {isPlaying ? <Pause size={16} /> : <VolumeOn size={16} />}
      </button>
    )}
  </div>
)

export const VideoVoiceoverPreference = ({ enabled, voice, onEnabledUpdate, onVoiceUpdate }: Props) => {
  const selectedVoice = voice || DEFAULT_VIDEO_VOICEOVER_VOICE
  const { playingKey, toggle } = useVoicePreview()

  const renderVoiceItems = (voices: typeof VIDEO_VOICEOVER_VOICES_FEMALE | typeof VIDEO_VOICEOVER_VOICES_MALE) =>
    voices.map((option) => (
      // @ts-expect-error withCheckmark is a custom prop read by Option internals
      <Item key={option.key} textValue={getVoiceLabel(option.key)} withCheckmark={false}>
        <VoiceItemContent
          voiceKey={option.key}
          isSelected={option.key === selectedVoice}
          isPlaying={playingKey === option.key}
          onToggle={toggle}
        />
      </Item>
    ))

  return (
    <div className={Styles.root} data-testid="video-voiceover-preference">
      <Pressable onPress={() => onEnabledUpdate(!enabled)}>
        <div className={Styles.label}>
          <div className={Styles.icon}>
            <Voice size={16} color="#8341DF" />
          </div>
          <Heading level={4} color="var(--dark-80)">
            Narrations
          </Heading>
        </div>
      </Pressable>
      <div className={Styles.right}>
        {enabled && (
          <div className={Styles.inlineControls}>
            <button
              type="button"
              className={Styles.speakerButton}
              aria-label={playingKey === selectedVoice ? `Pause ${selectedVoice}` : `Preview ${selectedVoice} voice`}
              onClick={(e) => {
                e.stopPropagation()
                toggle(selectedVoice)
              }}
            >
              {playingKey === selectedVoice ? <Pause size={16} /> : <VolumeOn size={16} />}
            </button>
            <Select
              aria-label="Video narration voice"
              variant="secondary"
              selectedKey={selectedVoice}
              onSelectionChange={(key) => onVoiceUpdate(key as string)}
              placeholder="Select voice"
              size="md"
              renderSelectValue={() => getVoiceLabel(selectedVoice)}
            >
              <Section title="Female">{renderVoiceItems(VIDEO_VOICEOVER_VOICES_FEMALE)}</Section>
              <Section title="Male">{renderVoiceItems(VIDEO_VOICEOVER_VOICES_MALE)}</Section>
            </Select>
          </div>
        )}
        <div onClick={() => onEnabledUpdate(!enabled)}>
          <VisualSwitch active={enabled} variant="md" />
        </div>
      </div>
    </div>
  )
}
